<template>
    <component :is="layout" />
    <Toast position="top-right" />
</template>

<script lang="ts" setup>
    import { Toast } from 'primevue'
    import { useToast } from 'primevue/usetoast'
    import { computed } from 'vue'
    import { useRoute, useRouter } from 'vue-router'

    import Blank from '@/layouts/Blank.vue'
    import Default from '@/layouts/Default.vue'
    import { installBuildGuard } from '@/libs/buildGuard'
    import i18n from '@/libs/i18n'
    import { isPublicPath } from '@/libs/publicRoutes'
    import { installSessionWatch } from '@/libs/sessionWatch'
    import { PrintWarning, registerToast } from '@/libs/toast'

    registerToast(useToast())

    // Re-check the session when the tab wakes up, so an expired JWT lands the user
    // on the login page instead of a UI where every click silently does nothing.
    installSessionWatch()

    const route  = useRoute()
    const router = useRouter()

    // A tab open across a deploy asks for lazy chunks that no longer exist; without
    // this the navigation just fails and the console stops responding to clicks.
    installBuildGuard(router, () => PrintWarning(i18n.global.t('errors.stale_build')))

    const layouts = { Default, Blank }

    const layout = computed(() => {
        const name = route.meta.layout as keyof typeof layouts | undefined
        if (name) return layouts[name]

        // Until the router has resolved the FIRST navigation, route.meta is still
        // empty. Defaulting to Default there mounted the whole authenticated shell
        // (Sidebar + its /mail/status poll + the layout's /me) for a tick on every
        // public page load — visible in the production log as 401 pairs with
        // Referer /login and /downloads. Public paths start on Blank instead.
        return isPublicPath() ? Blank : Default
    })
</script>
